import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";

export default function Auth() {
  const { login, register } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("buyer");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = mode === "login"
        ? await login(email, password)
        : await register({ email, password, name, role });
      toast.success(`Welcome${user.name ? `, ${user.name}` : ''}!`);
      if (user.role === "admin") navigate("/admin");
      else if (user.role === "seller") navigate("/vendor/dashboard");
      else navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Auth failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-16">
      <div className="text-center mb-8">
        <div className="badge-swadeshi inline-flex">{t("Namaste")}</div>
        <h1 className="text-4xl mt-4">{mode === "login" ? t("Welcome back") : t("Join the bazaar")}</h1>
        <p className="text-sm text-[#4A5568] mt-2">
          {mode === "login" ? t("Sign in to continue shopping.") : t("Create your account in 30 seconds.")}
        </p>
      </div>

      <form onSubmit={submit} className="card-flat p-8 space-y-4">
        {mode === "register" && (
          <>
            <div>
              <label className="label-caps">{t("Name")}</label>
              <input data-testid="auth-name" value={name} onChange={(e) => setName(e.target.value)} required className="mt-2 w-full px-3 py-2.5 border border-[#E2DCD0] rounded-lg focus:outline-none focus:border-[#E25A24]" />
            </div>
            <div>
              <label className="label-caps">{t("I want to")}</label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <button type="button" data-testid="role-buyer" onClick={() => setRole("buyer")} className={`py-2.5 rounded-lg border text-sm font-semibold ${role === "buyer" ? "bg-[#13294B] text-white border-[#13294B]" : "border-[#E2DCD0] text-[#13294B]"}`}>{t("Shop")}</button>
                <button type="button" data-testid="role-seller" onClick={() => setRole("seller")} className={`py-2.5 rounded-lg border text-sm font-semibold ${role === "seller" ? "bg-[#13294B] text-white border-[#13294B]" : "border-[#E2DCD0] text-[#13294B]"}`}>{t("Sell")}</button>
              </div>
            </div>
          </>
        )}
        <div>
          <label className="label-caps">{t("Email")}</label>
          <input data-testid="auth-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-2 w-full px-3 py-2.5 border border-[#E2DCD0] rounded-lg focus:outline-none focus:border-[#E25A24]" />
        </div>
        <div>
          <label className="label-caps">{t("Password")}</label>
          <input data-testid="auth-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="mt-2 w-full px-3 py-2.5 border border-[#E2DCD0] rounded-lg focus:outline-none focus:border-[#E25A24]" />
        </div>
        <button data-testid="auth-submit" disabled={loading} className="w-full btn-primary disabled:opacity-50">
          {loading ? t("Please wait…") : (mode === "login" ? t("Sign in") : t("Create account"))}
        </button>
        <button type="button" onClick={() => setMode(mode === "login" ? "register" : "login")} data-testid="auth-toggle" className="w-full text-sm text-[#4A5568] hover:text-[#E25A24]">
          {mode === "login" ? t("New here? Create an account →") : t("Already a member? Sign in →")}
        </button>
      </form>
    </div>
  );
}
